import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  AlertCircle,
  Upload,
  FileIcon,
  Trash2,
  Download,
  Eye,
  Calendar,
} from 'lucide-react';
import ProviderLayout from '@/components/layout/ProviderLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import apiService from '@/services/api';

interface Document {
  id: number;
  document_type: string;
  document_name: string;
  file_url: string;
  expires_at?: string;
  is_verified: boolean;
  created_at: string;
  updated_at: string;
  // Computed fields for UI
  file_name?: string;
  upload_date?: string;
  expiry_date?: string;
  status?: 'pending' | 'approved' | 'rejected' | 'expired';
}

const ProviderDocumentsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [documentType, setDocumentType] = useState('');
  const [certificateNumber, setCertificateNumber] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const hasCheckedAuth = useRef(false);

  useEffect(() => {
    if (!authLoading && hasCheckedAuth.current === false) {
      hasCheckedAuth.current = true;
      if (!user || user.role !== 'provider') {
        navigate('/provider/login');
      }
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user?.id && !authLoading) {
      fetchDocuments();
    }
  }, [user?.id, authLoading]);

  useEffect(() => {
    const dropZone = dropZoneRef.current;
    if (!dropZone) return;

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      dropZone.style.backgroundColor = '#f0f0f0';
    };

    const handleDragLeave = () => {
      dropZone.style.backgroundColor = 'white';
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      dropZone.style.backgroundColor = 'white';
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        handleFileUpload(files[0]);
      }
    };

    dropZone.addEventListener('dragover', handleDragOver);
    dropZone.addEventListener('dragleave', handleDragLeave);
    dropZone.addEventListener('drop', handleDrop);

    return () => {
      dropZone.removeEventListener('dragover', handleDragOver);
      dropZone.removeEventListener('dragleave', handleDragLeave);
      dropZone.removeEventListener('drop', handleDrop);
    };
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/provider/documents`);
      // Backend returns: {success: true, data: {documents: [...]}}
      const documentsList = response?.data?.documents || response?.documents || [];
      if (Array.isArray(documentsList)) {
        // Transform API response to component format
        const transformed = documentsList.map(transformDocument);
        setDocuments(transformed);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast({
        title: 'Error',
        description: 'Failed to load documents',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!documentType) {
      toast({
        title: 'Error',
        description: 'Please select a document type',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'Error',
        description: 'File size must be less than 10MB',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);
      if (certificateNumber) {
        formData.append('certificate_number', certificateNumber);
      }

      const response = await apiService.post(`/provider/documents/upload`, formData);
      if (response?.success) {
        toast({
          title: 'Success',
          description: 'Document uploaded successfully',
        });
        setShowUploadModal(false);
        setDocumentType('');
        setCertificateNumber('');
        fetchDocuments();
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload document',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    try {
      const response = await apiService.delete(`/provider/documents/${documentId}`);
      if (response?.success) {
        toast({
          title: 'Success',
          description: 'Document deleted successfully',
        });
        fetchDocuments();
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete document',
        variant: 'destructive',
      });
    }
  };

  const getStatusColor = (status?: string) => {
    // Handle undefined or null status
    if (!status) {
      return 'bg-gray-100 text-gray-800';
    }
    
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'expired':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Transform API response to component format
  const transformDocument = (doc: any): Document => ({
    ...doc,
    file_name: doc.document_name || doc.file_name,
    upload_date: doc.created_at,
    expiry_date: doc.expires_at,
    status: doc.is_verified ? 'approved' : 'pending'
  });

  const getExpiryStatus = () => {
    if (!documents.length) return null;
    const upcoming = documents
      .filter(d => d.expiry_date && d.status !== 'expired')
      .sort((a, b) => new Date(a.expiry_date!).getTime() - new Date(b.expiry_date!).getTime())[0];
    
    if (!upcoming) return null;
    const daysUntilExpiry = Math.ceil(
      (new Date(upcoming.expiry_date!).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );
    return { days: daysUntilExpiry, document: upcoming };
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const expiryStatus = getExpiryStatus();

  if (authLoading) {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Authenticating...</p>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  if (!user || user.role !== 'provider') {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Not authorized to access this page</p>
            <Button onClick={() => navigate('/provider/login')}>Go to Login</Button>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  return (
    <ProviderLayout>
      <Helmet>
        <title>Compliance Documents - VireWire Provider</title>
        <meta name="description" content="Manage your compliance documents" />
      </Helmet>

      <div className="space-y-8">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-gray-900">Compliance Documents</h1>
          <p className="text-gray-600">Manage and upload your compliance documents.</p>
        </div>

        {/* Expiry Status */}
        {expiryStatus && (
          <Card className="border-l-4 border-l-yellow-500 bg-yellow-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-900 mb-1">Expires in {expiryStatus.days} days</p>
                  <p className="text-sm text-gray-600">{expiryStatus.document.file_name}</p>
                </div>
                <Calendar className="w-8 h-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Documents List */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : documents.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center space-y-4">
              <FileIcon className="w-16 h-16 text-gray-300 mx-auto" />
              <div>
                <p className="text-gray-600 font-semibold">No documents uploaded</p>
                <p className="text-sm text-gray-500">Upload your compliance documents to get started.</p>
              </div>
              <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
                <DialogTrigger asChild>
                  <Button className="bg-green-600 hover:bg-green-700 text-white">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Document
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Upload Document</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="doc-type">Select a document type</Label>
                      <Select value={documentType} onValueChange={setDocumentType}>
                        <SelectTrigger id="doc-type">
                          <SelectValue placeholder="Choose document type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="insurance">Professional Insurance</SelectItem>
                          <SelectItem value="health_safety">Health & Safety Certificate</SelectItem>
                          <SelectItem value="gas_safe">Gas Safe Certificate</SelectItem>
                          <SelectItem value="dbs">DBS Certificate</SelectItem>
                          <SelectItem value="first_aid">First Aid Certificate</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="cert-num">Certificate Number</Label>
                      <Input
                        id="cert-num"
                        value={certificateNumber}
                        onChange={(e) => setCertificateNumber(e.target.value)}
                        placeholder="e.g., ABC123456"
                      />
                    </div>

                    <div
                      ref={dropZoneRef}
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-gray-400 bg-white"
                    >
                      <FileIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">
                        Drag a filing here or click a filing to browse
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        onChange={(e) => {
                          if (e.target.files && e.target.files.length > 0) {
                            handleFileUpload(e.target.files[0]);
                          }
                        }}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.jpg,.png"
                      />
                    </div>

                    <div className="flex gap-2 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => setShowUploadModal(false)}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        disabled={uploading || !documentType}
                        onClick={() => fileInputRef.current?.click()}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                        Upload File
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
                <DialogTrigger asChild>
                  <Button className="bg-green-600 hover:bg-green-700 text-white">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Document
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Upload Document</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="doc-type">Select a document type</Label>
                      <Select value={documentType} onValueChange={setDocumentType}>
                        <SelectTrigger id="doc-type">
                          <SelectValue placeholder="Choose document type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="insurance">Professional Insurance</SelectItem>
                          <SelectItem value="health_safety">Health & Safety Certificate</SelectItem>
                          <SelectItem value="gas_safe">Gas Safe Certificate</SelectItem>
                          <SelectItem value="dbs">DBS Certificate</SelectItem>
                          <SelectItem value="first_aid">First Aid Certificate</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="cert-num">Certificate Number</Label>
                      <Input
                        id="cert-num"
                        value={certificateNumber}
                        onChange={(e) => setCertificateNumber(e.target.value)}
                        placeholder="e.g., ABC123456"
                      />
                    </div>

                    <div
                      ref={dropZoneRef}
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-gray-400 bg-white"
                    >
                      <FileIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">
                        Drag a filing here or click a filing to browse
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        onChange={(e) => {
                          if (e.target.files && e.target.files.length > 0) {
                            handleFileUpload(e.target.files[0]);
                          }
                        }}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.jpg,.png"
                      />
                    </div>

                    <div className="flex gap-2 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => setShowUploadModal(false)}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        disabled={uploading || !documentType}
                        onClick={() => fileInputRef.current?.click()}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                        Upload File
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {documents.map((doc) => (
              <Card key={doc.id} className="border border-gray-200 hover:shadow-sm transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <FileIcon className="w-8 h-8 text-blue-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{doc.file_name}</p>
                        <p className="text-sm text-gray-600">{doc.document_type}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Badge className={getStatusColor(doc.status)}>
                        {(doc.status || 'pending').toUpperCase()}
                      </Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => window.open(doc.file_url, '_blank')}
                        className="text-gray-600"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = doc.file_url;
                          link.download = doc.file_name;
                          link.click();
                        }}
                        className="text-gray-600"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteDocument(doc.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </ProviderLayout>
  );
};

export default ProviderDocumentsPage;
